import { type IActions } from '@interface/IAction';
import type React from 'react';
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { type UseFormReturn } from 'react-hook-form';

import {
	type IConstituencyState,
	type ILocationLGAInState,
	type ILocationLGAsInState,
	type ILocationPollingUnitInWard,
	type ILocationPollingUnitsInWard,
	type ILocationStates,
	type ILocationWardData,
	type ILocationWardsInLGA,
} from '../../../../../interface/ILocation';
import { TypeButton } from '../../../../utils/button';
import type { LocationData, LocationType } from '../../../../utils/location-detail-modal/types';
import { TypeSelect } from '../../../../utils/select';
import { Separator } from '../../../../utils/styled';
import {
	type electionType,
	type filterKeys,
	type ILocationDetailsSchema,
	initFilterState,
	type newInitFilterState,
} from '../helper';
import { CTASection } from '../styled';
import { AccordionContainer } from './accordion-container';
import { parsePuCoordinate } from './geo';
import GeolocationCapture, { type GeoFixMeta } from './geolocation-capture';
import { RadioBoxItemsChild } from './radiobox-items-child';
import UnitDistanceMap from './UnitDistanceMap';

// The detail modal carries Leaflet (its own chunk) and is opened rarely — load
// it on first open rather than with the step.
const LocationDetailModal = lazy(() => import('../../../../utils/location-detail-modal'));

interface IPU {
	dataStates: ILocationStates | undefined;
	loadStates: boolean;
	datalga: ILocationLGAsInState | undefined;
	loadLga: boolean | undefined;
	dataWard: ILocationWardsInLGA | undefined;
	loadWard: boolean | undefined;
	dataPollingUnit: ILocationPollingUnitsInWard | undefined;
	loadPollingUnit: boolean | undefined;
	onNext: () => void;
	onPrevious: () => void;
	hookForm: UseFormReturn<ILocationDetailsSchema>;
	setSelectedState: React.Dispatch<React.SetStateAction<number>>;
	selectedState: number;
	currentElectionType: electionType;
	constituencyState: IConstituencyState | undefined;
	actions: IActions | undefined;
}

const Location: React.FC<IPU> = ({
	dataStates,
	loadStates,
	actions,
	dataPollingUnit,
	dataWard,
	datalga,
	loadLga,
	loadPollingUnit,
	loadWard,
	onNext,
	onPrevious,
	hookForm,
	selectedState,
	setSelectedState,
	currentElectionType,
	constituencyState,
}) => {
	const [filterState, setFilterState] = useState<newInitFilterState<typeof initFilterState>>(initFilterState);
	const [modalOpen, setModalOpen] = useState(false);
	const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(null);
	// Accuracy / capture time of the GPS fix (the form itself only holds lat/lng).
	const [fixMeta, setFixMeta] = useState<GeoFixMeta>({ accuracy: null, capturedAt: null });

	const CUSTOM_WIDTH = '100%';

	const { getPollingUnitsInWard, getLGAsInState, getWardsInLGA, clearAction } = actions!;

	const isNotPresident = currentElectionType !== 'President';

	const setPUCode = (id: number) => {
		try {
			const puData = dataPollingUnit?.data.find((i) => i.id === id);
			if (!puData) throw new Error('polling unit code not found');
			hookForm.setValue('entityCode', puData.poolingUnitCode, { shouldValidate: true });
			hookForm.setValue('entityName', puData.name, { shouldValidate: true });
			// Parsed unit coordinates for the Summary's "Distance from you". Not a
			// validated field and never posted (buildAndSubmit picks its keys).
			const coords = parsePuCoordinate(puData.latitude, puData.longitude);
			hookForm.setValue('puLatitude', coords?.lat ?? null);
			hookForm.setValue('puLongitude', coords?.lng ?? null);
		} catch (e) {
			console.error(e);
		}
	};

	const handleLocationInfo = (filterKey: filterKeys, value: number | string | null) => {
		switch (filterKey) {
			case 'stateId':
				// clearAction(locationType.lgasInState);
				// clearAction(locationType.wardsInLGA);
				// clearAction(locationType.pollingUnitsInWard);
				getLGAsInState({ stateIds: [value as number] });
				setFilterState((prev) => ({
					...prev,
					[filterKey]: value,
					// lgaId: null,
					// pollingUnitId: null,
					// wardId: null,
				}));
				break;
			case 'lgaId':
				// clearAction(locationType.wardsInLGA);
				// clearAction(locationType.pollingUnitsInWard);
				getWardsInLGA({ lgaIds: [value as number] });
				setFilterState((prev) => ({
					...prev,
					[filterKey]: value,
					// pollingUnitId: null,
					// wardId: null,
				}));
				break;
			case 'wardId':
				// clearAction(locationType.pollingUnitsInWard);
				getPollingUnitsInWard({ wardIds: [value as number] });
				hookForm.setValue('wardOfResidence', (value as number) ?? null, { shouldValidate: true });
				setFilterState((prev) => ({
					...prev,
					[filterKey]: value,
					// pollingUnitId: null,
				}));
				break;
			default:
				setFilterState((prev) => ({
					...prev,
					[filterKey]: value,
				}));
				setPUCode(Number(value));
				break;
		}
	};

	useEffect(() => {
		handleLocationInfo('stateId', constituencyState?.id || 0);
	}, [constituencyState?.id]);

	const initStateOption = isNotPresident
		? { label: constituencyState?.name || '', value: '' }
		: { label: 'Select State', value: '' };

	const getStateOptions = () => {
		if (!isNotPresident) {
			return dataStates?.data.map((i) => ({
				id: i.id,
				label: i.name,
				value: i.id,
			}));
		} else {
			return [
				{
					id: constituencyState?.id || 0,
					label: constituencyState?.name || '',
					value: constituencyState?.id || 0,
				},
			];
		}
	};

	const handleState = ({ target }: React.ChangeEvent<HTMLSelectElement>) => {
		const { value } = target;
		handleLocationInfo('stateId', parseInt(value));
		setSelectedState(Number(value));
	};

	const handleRadioState = ({ target }: React.ChangeEvent<HTMLInputElement>, key: filterKeys) => {
		const { id } = target;
		handleLocationInfo(key, parseInt(id));
	};

	const handleRadioContainerClick = (id: number, key: filterKeys) => {
		handleLocationInfo(key, id);
	};

	const handleLocationDetailClick = (data: any, name: filterKeys) => {
		let locationType: LocationType;
		let locationData: LocationData;

		switch (name) {
			case 'lgaId':
				locationType = 'LGA';
				locationData = { type: locationType, data: data as ILocationLGAInState };
				break;
			case 'wardId':
				locationType = 'Ward';
				locationData = { type: locationType, data: data as ILocationWardData };
				break;
			case 'pollingUnitId':
				locationType = 'Polling Unit';
				locationData = { type: locationType, data: data as ILocationPollingUnitInWard };
				break;
			default:
				return;
		}

		setSelectedLocation(locationData);
		setModalOpen(true);
	};

	const getParentInfo = () => {
		const stateName = constituencyState?.name || dataStates?.data.find((s) => s.id === selectedState)?.name;
		const lgaName = datalga?.data?.find((l) => l.id === filterState.lgaId)?.name;
		const wardName = dataWard?.data?.find((w) => w.id === filterState.wardId)?.name;
		return { stateName, lgaName, wardName };
	};

	const allLoader = loadLga || loadPollingUnit || loadStates || loadWard;

	// Derived from the LIVE polling-unit list, so changing ward (which reloads the
	// list) drops the card until a unit from the new ward is chosen.
	const selectedPu = useMemo(
		() => dataPollingUnit?.data.find((unit) => unit.id === filterState.pollingUnitId) ?? null,
		[dataPollingUnit, filterState.pollingUnitId]
	);
	const userLatitude = hookForm.watch('latitude');
	const userLongitude = hookForm.watch('longitude');

	const getSelectedName = (name: string, data?: any, id?: number | string) => {
		if (!data || !id) return '';
		if (!data?.filter((i: any) => i.id === id)[0]) return '';
		return data?.filter((i: any) => i.id === id)[0].name;
	};

	const dataProps = useMemo(
		() => [
			{
				name: 'lgaId',
				title: 'LGA',
				selectedName: getSelectedName('LGA', datalga?.data, filterState.lgaId || 0),
				data: datalga?.data,
				checkedId: filterState.lgaId,
				load: loadLga,
			},
			{
				name: 'wardId',
				title: 'Ward',
				selectedName: getSelectedName('Ward', dataWard?.data, filterState.wardId || 0),
				data: dataWard?.data,
				checkedId: filterState.wardId,
				load: loadWard,
			},
			{
				name: 'pollingUnitId',
				title: 'Polling Unit',
				selectedName: getSelectedName('Polling Unit', dataPollingUnit?.data, filterState.pollingUnitId || 0),
				data: dataPollingUnit?.data,
				checkedId: filterState.pollingUnitId,
				load: loadPollingUnit,
			},
		],
		[datalga, dataWard, dataPollingUnit, filterState, getSelectedName]
	);

	return (
		<>
			<div>
				<GeolocationCapture
					hookForm={hookForm}
					hasPollingUnit={Boolean(selectedPu)}
					onFixChange={setFixMeta}
				/>
				<TypeSelect
					initoption={initStateOption}
					label='State'
					customwidth={CUSTOM_WIDTH}
					optionsdata={getStateOptions()}
					load={allLoader}
					disabled={allLoader || isNotPresident}
					error=''
					onChange={handleState}
					className='w-100'
					value={selectedState}
				/>
				<Separator customheight={20} />
				{dataProps.map((i, index) =>
					i.data ? (
						<div key={index}>
							<AccordionContainer
								title={i.title}
								selectedName={i.selectedName}
							>
								<RadioBoxItemsChild
									allLoader={allLoader || false}
									handleRadioContainerClick={handleRadioContainerClick}
									handleRadioState={handleRadioState}
									i={i}
									onTextClick={handleLocationDetailClick}
								/>
							</AccordionContainer>
							{i.name === 'pollingUnitId' && selectedPu ? (
								<div style={{ marginTop: 12 }}>
									<UnitDistanceMap
										pu={selectedPu}
										userLatitude={userLatitude}
										userLongitude={userLongitude}
										accuracyMeters={fixMeta.accuracy}
									/>
								</div>
							) : null}
							<Separator customheight={20} />
						</div>
					) : null
				)}
			</div>

			<CTASection>
				<TypeButton
					title='← Previous'
					onClick={onPrevious}
					className='prevbtn'
				/>
				<TypeButton
					title='Next →'
					onClick={onNext}
					disabled={!hookForm.formState.isValid}
					className='nextbtn'
				/>
			</CTASection>

			{modalOpen ? (
				<Suspense fallback={null}>
					<LocationDetailModal
						isOpen={modalOpen}
						onClose={() => setModalOpen(false)}
						location={selectedLocation}
						parentInfo={getParentInfo()}
					/>
				</Suspense>
			) : null}
		</>
	);
};

export default Location;
